import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const GamificationContext = createContext();

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

// Calcule le niveau à partir des XP totaux
function computeLevel(totalXp) {
  return Math.floor(totalXp / 500) + 1;
}

export const GamificationProvider = ({ children }) => {
  const { token } = useAuth();
  const [userStats, setUserStats] = useState({});
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadGamificationData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        axios.get('/api/gamification/my-stats').catch(() => null),
        axios.get('/api/gamification/leaderboard?limit=10').catch(() => null),
      ]);

      // Traiter les stats
      if (statsRes?.data?.success) {
        const { streak, badges: badgeData } = statsRes.data;
        const totalXp = streak?.total_xp || 0;
        const level = computeLevel(totalXp);

        setUserStats({
          totalPoints: totalXp,
          level,
          experience: totalXp,
          experienceToNext: level * 500,
          coursesCompleted: 0, // Non fourni par cet endpoint (sera enrichi si disponible)
          certificatesEarned: 0,
          streak: streak?.current_streak || 0,
          longestStreak: streak?.longest_streak || 0,
          badgesEarned: (badgeData?.earned || []).length,
          rank: null,
          totalStudyTime: 0,
          thisWeekStudyTime: 0,
          achievements: [],
        });

        // Construire la liste des badges combinant earned et available
        const earnedIds = new Set((badgeData?.earned || []).map(b => b.badge_id || b.id));
        const earnedMap = {};
        (badgeData?.earned || []).forEach(b => {
          const badge = b.badge || b;
          earnedMap[badge.id] = b.earned_at || b.created_at;
        });

        const allBadges = (badgeData?.available || []).map(b => ({
          id: b.id,
          name: b.name || b.title,
          description: b.description,
          icon: b.icon || '🏅',
          rarity: b.rarity || 'common',
          points: b.xp_reward || b.points || 0,
          category: b.category || 'learning',
          unlocked: earnedIds.has(b.id),
          unlockedAt: earnedMap[b.id] || null,
        }));

        setBadges(allBadges);
      } else {
        // Pas de données (non connecté ou tables vides)
        setUserStats({
          totalPoints: 0, level: 1, experience: 0, experienceToNext: 500,
          coursesCompleted: 0, certificatesEarned: 0, streak: 0, longestStreak: 0,
          badgesEarned: 0, rank: null, totalStudyTime: 0, thisWeekStudyTime: 0,
          achievements: [],
        });
        setBadges([]);
      }

      // Traiter le leaderboard
      if (leaderboardRes?.data?.success) {
        const lb = (leaderboardRes.data.leaderboard || []).map((entry, index) => ({
          id: entry.user_id,
          name: entry.user
            ? `${entry.user.first_name || ''} ${entry.user.last_name || ''}`.trim() || 'Utilisateur'
            : 'Utilisateur',
          avatar: entry.user?.avatar || null,
          points: entry.total_xp || 0,
          level: computeLevel(entry.total_xp || 0),
          coursesCompleted: entry.courses_completed || 0,
          streak: entry.current_streak || 0,
          rank: index + 1,
          isCurrentUser: false,
        }));
        setLeaderboard(lb);
      } else {
        setLeaderboard([]);
      }
    } catch (err) {
      console.error('[GamificationContext] Erreur chargement:', err);
      setUserStats({
        totalPoints: 0, level: 1, experience: 0, experienceToNext: 500,
        coursesCompleted: 0, certificatesEarned: 0, streak: 0, longestStreak: 0,
        badgesEarned: 0, rank: null, totalStudyTime: 0, thisWeekStudyTime: 0,
        achievements: [],
      });
      setBadges([]);
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) loadGamificationData(); }, [token, loadGamificationData]);

  const getUnlockedBadges = () => badges.filter(badge => badge.unlocked);

  const getLockedBadges = () => badges.filter(badge => !badge.unlocked);

  const getBadgesByCategory = (category) => badges.filter(badge => badge.category === category);

  const getBadgesByRarity = (rarity) => badges.filter(badge => badge.rarity === rarity);

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':     return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'uncommon':   return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rare':       return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'epic':       return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'legendary':  return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:           return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getRarityName = (rarity) => {
    switch (rarity) {
      case 'common':    return 'Commun';
      case 'uncommon':  return 'Peu commun';
      case 'rare':      return 'Rare';
      case 'epic':      return 'Épique';
      case 'legendary': return 'Légendaire';
      default:          return 'Commun';
    }
  };

  const getLevelProgress = () => {
    const { experience, level } = userStats;
    if (!experience || !level) return 0;
    const currentLevelBase = (level - 1) * 500;
    const nextLevelBase = level * 500;
    return Math.min(100, ((experience - currentLevelBase) / (nextLevelBase - currentLevelBase)) * 100);
  };

  const getTopUsers = (limit = 10) => leaderboard.slice(0, limit);

  const getUserRank = () => {
    const currentUser = leaderboard.find(u => u.isCurrentUser);
    return currentUser ? currentUser.rank : userStats.rank;
  };

  const addPoints = (points) => {
    setUserStats(prev => ({
      ...prev,
      totalPoints: (prev.totalPoints || 0) + points,
      experience: (prev.experience || 0) + points,
    }));
  };

  const unlockBadge = (badgeId) => {
    setBadges(prev => prev.map(badge =>
      badge.id === badgeId
        ? { ...badge, unlocked: true, unlockedAt: new Date().toISOString().split('T')[0] }
        : badge
    ));
  };

  const value = {
    userStats,
    badges,
    achievements: userStats.achievements || [],
    leaderboard,
    loading,
    getUnlockedBadges,
    getLockedBadges,
    getBadgesByCategory,
    getBadgesByRarity,
    getRarityColor,
    getRarityName,
    getLevelProgress,
    getTopUsers,
    getUserRank,
    addPoints,
    unlockBadge,
    loadGamificationData,
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};
